import { type ReactNode } from 'react';
import cn from 'classnames';
import Tooltip, { type TooltipPlacement } from '@creditflowresearch/uilib/Tooltip';
import IconLock from '@creditflowresearch/uilib/Icon/IconLock';

import styles from './badgeButton.module.scss';

interface IProps<P = string> {
  label: string,
  value: P,
  dataTest: string,
  onClick: (value: P) => void,
  tooltip?: ReactNode,
  isActive?: boolean,
  isDisabled?: boolean,
  isLoading?: boolean,
  isMobile?: boolean,
  className?: string,
  tooltipClassName?: string,
  tooltipPlacement?: TooltipPlacement,
}

const BadgeButton = <P = string>({
  label,
  value,
  dataTest,
  onClick,
  tooltip = 'Set as default. This will determine the page you land on at login.',
  isActive,
  isDisabled,
  isLoading,
  isMobile = false,
  className,
  tooltipClassName,
  tooltipPlacement,
}: IProps<P>) => {
  const button = (
    <button
      type="button"
      onClick={ () => !isDisabled && onClick(value) }
      data-test={ dataTest }
      aria-pressed={ !!isActive }
      className={ cn(
      styles.badgeButton,
      { [styles.badgeButtonActive]: isActive },
      { [styles.badgeButtonDisabled]: isDisabled },
      { [styles.badgeButtonLoading]: isLoading },
      className,
    ) }
    >
      { isDisabled && <IconLock className={ styles.iconLock } /> }
      { label }
    </button>
  );

  if (isDisabled) {
    return button;
  }

  return (
    <Tooltip
      className={ cn(styles.badgeButtonTooltip, tooltipClassName) }
      content={ tooltip }
      placement={ tooltipPlacement }
      disabled={ isActive || isMobile }
    >
      { button }
    </Tooltip>
  );
};

export default BadgeButton;
